/* eslint-disable react-hooks/rules-of-hooks */
import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useLocation } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Badge from 'components/Badge';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import Typography from '@material-ui/core/Typography';
import { getImageFile } from 'utils/stringUtils';
import ButtonBase from '@material-ui/core/ButtonBase';
import ClassNames from 'classnames';
import useContentfulLocale from 'components/useContentfulLocale';
import { getHeroResourceTitle } from 'components/HeroLanding/utils';
import { useSelector } from 'react-redux';
import {
  makeSelectClientDetails,
  makeSelectSiteCopyInOtherLang,
  makeSelectTextDirection,
} from 'containers/Main/selectors';
import HeroContext from '../../PageContext';
import getServiceType from '../../utils/getServiceType';
import getFormattedType from '../../utils/getFormattedType';

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
    display: 'block',
    marginRight: ({ isTablet }) => isTablet && 10,
  },
  root: {
    background: '#fff',
    boxShadow: '0 0 29px 0 rgba(0,0,0,0.10)',
    borderRadius: 4,
    width: '100%',
    height: 84,
    display: 'flex',
    marginBottom: 20,
    overflow: 'hidden',
    zIndex: 99,
  },
  variantE: {
    marginBottom: 8,
  },
  imgBG: {
    width: 84,
    height: 84,
    background: props =>
      `url(${props.imageSrc}) no-repeat center / ${props.backgroundSize}`,
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  title: {
    fontFamily: 'PoppinsSemiBold',
    fontSize: 16,
    letterSpacing: 0,
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    direction: ({ textDirection }) => textDirection,
  },
  longTitle: {
    '-webkit-line-clamp': '2',
  },
  description: {
    fontSize: 12,
    letterSpacing: 0,
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: '0.85rem',
  },
  ratingWrapper: {
    '& div': {
      maxWidth: '100%',
      flexBasis: '100%',
    },
    '& p': {
      fontSize: 9.6,
    },
  },
  info: {
    padding: '8px 18px',
  },
  badgeWrapper: {
    '& div': {
      width: '100%',
      margin: 0,
      height: 20,
    },
  },
  grayscale: {
    filter: 'grayscale(1)',
  },
}));
function SecondCard({
  data,
  clickHandler,
  variant,
  locale,
  otherLanguage,
  isTablet,
}) {
  if (_isEmpty(data)) return null;
  const imageSrc = getImageFile(data);
  const isVariantE = variant === 'E';
  const descriptionRef = useRef(null);
  const infoContainerRef = useRef(null);
  const history = useHistory();
  const location = useLocation();
  const { landingInfo } = useContext(HeroContext);
  const {
    title,
    slug,
    name,
    article,
    description,
    shortDescription,
    url,
    subtitle = '',
    __typename = '',
  } = data;
  const routeType = getServiceType(data);
  const ctfLocaleFilter = useContentfulLocale();
  const siteCopyInOtherLang = useSelector(makeSelectSiteCopyInOtherLang());
  const clientDetails = useSelector(makeSelectClientDetails());
  const textDirection = useSelector(makeSelectTextDirection());
  const type = getFormattedType(data);
  const heroResourceTitle = getHeroResourceTitle({
    type,
    title,
    name,
    article,
  });
  const finalImageSrc =
    routeType === 'landing' ? _get(data, 'introMedia.url') : imageSrc;
  const displayNewLinkCard = _get(
    clientDetails,
    'metadata.displayNewLinkCard',
    false,
  );
  const classes = useStyles({
    imageSrc: finalImageSrc,
    isTablet,
    backgroundSize:
      type === 'link' && !displayNewLinkCard ? 'contain' : 'cover',
    textDirection,
  });
  const link = `/${routeType}/${slug}?lang=${locale || ctfLocaleFilter.locale}`;
  const isAssessment = type === 'assessment';
  const isActivity = __typename === 'Activity' || type === 'articles';

  useEffect(() => {
    requestAnimationFrame(() => {
      if (
        infoContainerRef.current &&
        descriptionRef.current &&
        descriptionRef.current.offsetHeight + descriptionRef.current.offsetTop >
          infoContainerRef.current.offsetHeight
      ) {
        descriptionRef.current.style.display = 'none';
      }
    });
  }, []);

  const redirect = () => {
    if (type === 'source' || type === 'link') {
      const isInternal =
        url && (url.includes('crediblemind.com') || url.startsWith('/'));
      if (isInternal) {
        const redirectUrl = isActivity
          ? `/articles/${slug}?lang=${locale || ctfLocaleFilter.locale}`
          : url.split('crediblemind.com')[1] || url;
        clickHandler('Client Resource Card Clicked - Home', {
          article: heroResourceTitle,
          type,
          slug,
          resource: redirectUrl,
        });
        history.push(redirectUrl);
      } else {
        clickHandler('Client Resource Card Exited - Home', {
          name: heroResourceTitle,
          type,
          slug,
          exitPage: url,
        });
        window.open(url, '_blank', 'noopener');
      }
    } else {
      const pageName = location.pathname.split('/')[1];
      const page = pageName ? `${pageName}Page` : 'homePage';

      clickHandler('Resource Card - Clicked', {
        name: heroResourceTitle,
        type,
        slug,
        page,
        ...(page === 'homePage' ? { path: 'banner' } : {}),
      });
      history.push(link);
    }
  };
  const isTopic = type === 'topic';
  const shouldApplyGrayscaleFilter = _get(
    landingInfo,
    'pageText.grayscale',
    false,
  );
  return (
    <ButtonBase onClick={redirect} disableRipple className={classes.container}>
      <Grid
        item
        xs={12}
        className={ClassNames(classes.root, { [classes.variantE]: isVariantE })}
      >
        <div>
          <div
            className={ClassNames(classes.imgBG, {
              [classes.grayscale]: shouldApplyGrayscaleFilter,
            })}
          ></div>
        </div>
        <div className={classes.info} ref={infoContainerRef}>
          <div className={classes.badgeWrapper}>
            <Badge
              type={type}
              data={data}
              siteCopyInOtherLang={otherLanguage && siteCopyInOtherLang}
            />
          </div>
          <Typography
            className={ClassNames(classes.title, {
              [classes.longTitle]: !isTopic && !isAssessment,
            })}
            align="left"
            color="textPrimary"
          >
            {isAssessment ? name : heroResourceTitle}
          </Typography>
          {(isTopic || type === 'list') && !isTablet && (
            <Typography
              className={classes.description}
              ref={descriptionRef}
              color="textPrimary"
              align="left"
            >
              {description || shortDescription}
            </Typography>
          )}
          {(type === 'source' || type === 'link') && !isTablet && (
            <Typography
              className={classes.description}
              ref={descriptionRef}
              color="textPrimary"
              align="left"
            >
              {subtitle}
            </Typography>
          )}
        </div>
      </Grid>
    </ButtonBase>
  );
}

SecondCard.propTypes = {
  data: PropTypes.object,
  clickHandler: PropTypes.func,
  variant: PropTypes.string,
};

export default SecondCard;
